import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, FolderPlus, Folder } from "lucide-react";
import "./Dashboard.css";

/**
 * AddToProjectDialog - Dialog to move a chat to an existing project (or remove from project)
 * @param {boolean} open - Whether dialog is open
 * @param {Object} anchorRect - Optional { top, left, bottom, right } to position dialog near the chat row
 * @param {string} chatId - ID of the chat being moved
 * @param {string} currentProjectId - Current project ID the chat belongs to (or null)
 * @param {Array} projects - List of { _id, name }
 * @param {Function} onSelect - (chatId, projectId) => void; projectId null = remove from project
 * @param {Function} onClose - () => void
 * @param {boolean} darkMode - Current dark mode state
 * @param {string} label - Optional label e.g. "Chat" or "Debate"
 */
export default function AddToProjectDialog({
  open,
  anchorRect,
  chatId,
  currentProjectId = null,
  projects = [],
  onSelect,
  onClose,
  darkMode = false,
  label = "Chat",
}) {
  const listRef = useRef(null);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.querySelector("[data-selected='true']");
      if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [open, currentProjectId]);

  if (!open || typeof document === "undefined" || !document.body) return null;

  const handleSelect = (projectId) => {
    onSelect(chatId, projectId);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  const dialogWidth = 760;
  const dialogContent = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000000,
        pointerEvents: "none",
      }}
    >
      <motion.div
        role="dialog"
        aria-label={`Add ${label} to project`}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.15 }}
        className="add-to-project-dialog"
        style={{
          width: `${dialogWidth}px`,
          maxWidth: "90vw",
          maxHeight: "90vh",
          pointerEvents: "auto",
          backgroundColor: darkMode ? "#1e293b" : "#ffffff",
        border: `1px solid ${darkMode ? "#475569" : "#e5e7eb"}`,
        borderRadius: "0.5rem",
        boxShadow: darkMode
          ? "0 10px 25px -5px rgba(0, 0, 0, 0.5)"
          : "0 10px 25px -5px rgba(0, 0, 0, 0.15)",
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={handleKeyDown}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "0.25rem",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: darkMode ? "#f1f5f9" : "#1e293b",
          }}
        >
          Add to project
        </h3>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0.25rem",
            color: darkMode ? "#94a3b8" : "#64748b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: "0.8125rem",
          color: darkMode ? "#94a3b8" : "#64748b",
        }}
      >
        Move this {label.toLowerCase()} to a project
      </p>
      <div
        ref={listRef}
        style={{
          overflowY: "auto",
          maxHeight: "560px",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <button
          type="button"
          data-selected={currentProjectId === null ? "true" : undefined}
          onClick={() => handleSelect(null)}
          style={{
            width: "100%",
            padding: "0.5rem 0.75rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            background: currentProjectId === null ? (darkMode ? "#334155" : "#f1f5f9") : "transparent",
            border: "none",
            borderRadius: "0.375rem",
            cursor: "pointer",
            fontSize: "0.875rem",
            color: darkMode ? "#e2e8f0" : "#334155",
            textAlign: "left",
          }}
        >
          <Folder size={16} style={{ flexShrink: 0, opacity: 0.8 }} />
          No project
        </button>
        {projects.map((p) => (
          <button
            key={p._id}
            type="button"
            data-selected={currentProjectId === p._id ? "true" : undefined}
            onClick={() => handleSelect(p._id)}
            style={{
              width: "100%",
              padding: "0.5rem 0.75rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              background: currentProjectId === p._id ? (darkMode ? "#334155" : "#f1f5f9") : "transparent",
              border: "none",
              borderRadius: "0.375rem",
              cursor: "pointer",
              fontSize: "0.875rem",
              color: darkMode ? "#e2e8f0" : "#334155",
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <FolderPlus size={16} style={{ flexShrink: 0, opacity: 0.8 }} />
            {p.name || "Unnamed project"}
          </button>
        ))}
      </div>
      {projects.length === 0 && (
        <p
          style={{
            margin: 0,
            fontSize: "0.8125rem",
            color: darkMode ? "#94a3b8" : "#64748b",
          }}
        >
          No projects yet. Create one from the sidebar.
        </p>
      )}
    </motion.div>
    </div>
  );

  return createPortal(
    <>
      <div
        role="presentation"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999999,
          backgroundColor: "rgba(0,0,0,0.2)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
        onClick={onClose}
        onMouseDown={onClose}
      />
      {dialogContent}
    </>,
    document.body
  );
}
